export interface CardPalette {
  /** Darkest stop at the bottom of the product-card scrim. */
  base: string;
  /** Mid stop, from a secondary hue in the image when it has one. */
  tint: string;
  /** Bright version of the base hue for the seller/category text; always ≥4.8:1 against the tint. */
  highlight: string;
  /** Card outline: a subtle, dim version of the base hue (≥2:1 against the page). */
  outline: string;
  /** Product title: near-white with a faint tint of the base hue. */
  title: string;
  /** Tag chip fill: the base hue lifted clear of the scrim base it sits on (≥1.4:1). */
  chip: string;
  /** Tag chip label: always ≥4.5:1 against the chip fill. */
  chipText: string;
}

export type Rgb = [red: number, green: number, blue: number];

const HUE_BINS = 12;
const SAMPLE_SIZE = 24;
// Scrim colours stay dark and muted so overlay text keeps its contrast and the image stays the brightest element.
const BASE_LIGHTNESS = 0.1;
const TINT_LIGHTNESS = 0.17;
const MAX_SATURATION = 0.5;
const MIN_TEXT_CONTRAST = 4.5;
// Seller/category text sits a touch above the text minimum so it reads clearly on the gradient.
const HIGHLIGHT_CONTRAST = 4.8;
// Lightness ranges (percent) searched upwards until a contrast target is met. They start low and saturation is
// capped, so colours only get as bright as legibility requires.
// Starts at 64% so the darker gradient can't drag the seller/category text back down with it.
const HIGHLIGHT_RANGE = { from: 64, to: 96, maxSaturation: 0.6 };
const CHIP_RANGE = { from: 22, to: 40, maxSaturation: 0.3 };
// Tag labels stay muted: less saturated than the text highlight, and only as light as 4.5:1 on the chip requires.
const CHIP_TEXT_RANGE = { from: 56, to: 96, maxSaturation: 0.35 };
// Chips are labels, not controls: enough separation to read as a shape on the scrim without competing with the text.
const MIN_CHIP_SEPARATION = 1.4;
// The outline is a subtle hover edge, not text and not the only hover cue (the image defines the card; the tag reveal
// and scrim carry the state), so it only needs to stay perceptible against the page.
const OUTLINE_RANGE = { from: 26, to: 60, maxSaturation: 0.35 };
const MIN_OUTLINE_CONTRAST = 2;
// Mirrors --color-bg, the page the card outline is drawn against.
const PAGE_BACKGROUND: Rgb = [10, 10, 15];
// The title stays near-white; the tint is only a faint drop of the image hue.
const TITLE_LIGHTNESS = 0.93;
const TITLE_MAX_SATURATION = 0.4;

export type Hsl = [hue: number, saturation: number, lightness: number];
type Color = { css: string; rgb: Rgb };

function rgbToHsl(red: number, green: number, blue: number): Hsl {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return [0, 0, lightness];
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue: number;
  if (max === r) hue = ((g - b) / delta) % 6;
  else if (max === g) hue = (b - r) / delta + 2;
  else hue = (r - g) / delta + 4;
  return [(hue * 60 + 360) % 360, saturation, lightness];
}

/** `hue` in degrees, `saturation` and `lightness` in 0–1. */
export function hslToRgb(hue: number, saturation: number, lightness: number): Rgb {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const segment = (((hue % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((segment % 2) - 1));
  const [r, g, b] =
    segment < 1 ? [chroma, second, 0]
    : segment < 2 ? [second, chroma, 0]
    : segment < 3 ? [0, chroma, second]
    : segment < 4 ? [0, second, chroma]
    : segment < 5 ? [second, 0, chroma]
    : [chroma, 0, second];
  const offset = lightness - chroma / 2;
  return [Math.round((r + offset) * 255), Math.round((g + offset) * 255), Math.round((b + offset) * 255)];
}

function relativeLuminance([red, green, blue]: Rgb) {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue);
}

/** WCAG contrast ratio between two colours. */
export function contrastRatio(first: Rgb, second: Rgb) {
  const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort((a, b) => b - a) as [number, number];
  return (lighter + 0.05) / (darker + 0.05);
}

/** Rounds to the values the CSS string renders, so contrast is measured on the colour users actually see. */
function hslColor(hue: number, saturation: number, lightness: number): Color {
  const h = Math.round(hue) % 360;
  const s = Math.round(saturation * 100);
  const l = Math.round(lightness * 100);
  return { css: `hsl(${h} ${s}% ${l}%)`, rgb: hslToRgb(h, s / 100, l / 100) };
}

function scrimColor([hue, saturation]: Hsl, lightness: number) {
  return hslColor(hue, Math.min(saturation, MAX_SATURATION), lightness);
}

/** Walks lightness up in 2% steps until the colour reaches `minimum` contrast against `background`. */
function liftUntilContrast(
  [hue, saturation]: Hsl,
  range: { from: number; to: number; maxSaturation: number },
  background: Rgb,
  minimum: number,
): Color {
  const cappedSaturation = Math.min(saturation, range.maxSaturation);
  let color = hslColor(hue, cappedSaturation, range.from / 100);
  for (let lightness = range.from; lightness <= range.to; lightness += 2) {
    color = hslColor(hue, cappedSaturation, lightness / 100);
    if (contrastRatio(color.rgb, background) >= minimum) break;
  }
  return color;
}

function buildPalette(dominant: Hsl, secondary: Hsl): CardPalette {
  const base = scrimColor(dominant, BASE_LIGHTNESS);
  const tint = scrimColor(secondary, TINT_LIGHTNESS);
  // Measured against the tint — the lighter scrim stop behind the seller row — so it stays legible over the gradient.
  const highlight = liftUntilContrast(dominant, HIGHLIGHT_RANGE, tint.rgb, HIGHLIGHT_CONTRAST);
  // Tags sit on the solid base of the scrim: the fill separates from it, and the label is legible on the fill.
  const chip = liftUntilContrast(dominant, CHIP_RANGE, base.rgb, MIN_CHIP_SEPARATION);
  const chipText = liftUntilContrast(dominant, CHIP_TEXT_RANGE, chip.rgb, MIN_TEXT_CONTRAST);
  // The outline sits on the card edge against the page, so it is measured against the page background.
  const outline = liftUntilContrast(dominant, OUTLINE_RANGE, PAGE_BACKGROUND, MIN_OUTLINE_CONTRAST);
  const title = hslColor(dominant[0], Math.min(dominant[1], TITLE_MAX_SATURATION), TITLE_LIGHTNESS);
  return {
    base: base.css,
    tint: tint.css,
    highlight: highlight.css,
    outline: outline.css,
    title: title.css,
    chip: chip.css,
    chipText: chipText.css,
  };
}

/**
 * Bins RGBA pixel data into hues weighted by chroma, and picks the most
 * colourful hue plus a clearly different secondary hue (if the image has
 * one). Grayscale images fall back to their neutral average for both.
 * Returns null only when every pixel is transparent.
 */
function extractHues(pixels: ArrayLike<number>): { dominant: Hsl; secondary: Hsl } | null {
  const bins = Array.from({ length: HUE_BINS }, (_, index) => ({ index, weight: 0, red: 0, green: 0, blue: 0 }));
  let opaque = 0;
  let red = 0;
  let green = 0;
  let blue = 0;

  for (let index = 0; index + 3 < pixels.length; index += 4) {
    const r = pixels[index] ?? 0;
    const g = pixels[index + 1] ?? 0;
    const b = pixels[index + 2] ?? 0;
    if ((pixels[index + 3] ?? 0) < 200) continue;
    opaque++;
    red += r;
    green += g;
    blue += b;

    const [hue, saturation, lightness] = rgbToHsl(r, g, b);
    // Chroma: zero for grays, blacks and whites, highest for vivid mid-tones.
    const weight = saturation * (1 - Math.abs(2 * lightness - 1));
    if (weight < 0.08) continue;
    const bin = bins[Math.floor(hue / (360 / HUE_BINS)) % HUE_BINS];
    if (!bin) continue;
    bin.weight += weight;
    bin.red += r * weight;
    bin.green += g * weight;
    bin.blue += b * weight;
  }

  if (!opaque) return null;
  const ranked = bins.filter((bin) => bin.weight > 0).sort((a, b) => b.weight - a.weight);
  const dominant = ranked[0];
  if (!dominant) {
    const neutral = rgbToHsl(red / opaque, green / opaque, blue / opaque);
    return { dominant: neutral, secondary: neutral };
  }

  const secondary = ranked.find((bin) => {
    const distance = Math.abs(bin.index - dominant.index);
    return Math.min(distance, HUE_BINS - distance) >= 2 && bin.weight >= dominant.weight * 0.15;
  }) ?? dominant;
  const average = (bin: typeof dominant) => rgbToHsl(bin.red / bin.weight, bin.green / bin.weight, bin.blue / bin.weight);
  return { dominant: average(dominant), secondary: average(secondary) };
}

/** Picks the product-card palette from RGBA pixel data — see `extractHues`. */
export function paletteFromPixels(pixels: ArrayLike<number>): CardPalette | null {
  const hues = extractHues(pixels);
  return hues && buildPalette(hues.dominant, hues.secondary);
}

let sampleCanvas: HTMLCanvasElement | undefined;

/** Draws a loaded image onto the shared sample canvas and reads back its pixels. */
function samplePixels(image: HTMLImageElement): ArrayLike<number> | null {
  try {
    sampleCanvas ??= document.createElement("canvas");
    sampleCanvas.width = SAMPLE_SIZE;
    sampleCanvas.height = SAMPLE_SIZE;
    const context = sampleCanvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    return context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
  } catch {
    return null;
  }
}

const paletteCache = new Map<string, CardPalette | null>();

export function cachedPalette(imageKey: string) {
  return paletteCache.get(imageKey) ?? null;
}

/** Samples a loaded image once per image key; returns null when the pixels cannot be read. */
export function paletteFromImage(imageKey: string, image: HTMLImageElement): CardPalette | null {
  if (paletteCache.has(imageKey)) return cachedPalette(imageKey);
  const pixels = samplePixels(image);
  const palette = pixels ? paletteFromPixels(pixels) : null;
  paletteCache.set(imageKey, palette);
  return palette;
}

/** Two-stop wash for a full-bleed ambient background (DESIGN.md PDP hero glow): brighter and a
 * touch more saturated than the card scrim, since it's diluted by transparency in the gradient
 * that consumes it rather than sitting directly under text. */
export interface AmbientGlow {
  /** Strongest stop, from the dominant hue. */
  base: string;
  /** Softer stop the wash settles into before fading out, from the secondary hue. */
  tint: string;
}

const GLOW_BASE_LIGHTNESS = 0.24;
const GLOW_TINT_LIGHTNESS = 0.3;
const GLOW_MAX_SATURATION = 0.65;

function glowStop([hue, saturation]: Hsl, lightness: number): Color {
  return hslColor(hue, Math.min(saturation, GLOW_MAX_SATURATION), lightness);
}

/** Picks the PDP ambient-glow colours from RGBA pixel data — see `extractHues`. */
export function ambientGlowFromPixels(pixels: ArrayLike<number>): AmbientGlow | null {
  const hues = extractHues(pixels);
  if (!hues) return null;
  return { base: glowStop(hues.dominant, GLOW_BASE_LIGHTNESS).css, tint: glowStop(hues.secondary, GLOW_TINT_LIGHTNESS).css };
}

const glowCache = new Map<string, AmbientGlow | null>();

export function cachedAmbientGlow(imageKey: string) {
  return glowCache.get(imageKey) ?? null;
}

/** Samples a loaded image once per image key; returns null when the pixels cannot be read. */
export function ambientGlowFromImage(imageKey: string, image: HTMLImageElement): AmbientGlow | null {
  if (glowCache.has(imageKey)) return cachedAmbientGlow(imageKey);
  const pixels = samplePixels(image);
  const glow = pixels ? ambientGlowFromPixels(pixels) : null;
  glowCache.set(imageKey, glow);
  return glow;
}
